/**
 * mdlondon - Image Optimization
 * Handles WebP/AVIF detection, dynamic loading, and blur-up placeholders
 */

class ImageOptimizer {
  constructor() {
    this.formats = {
      avif: false,
      webp: false
    };
    this.init();
  }

  async init() {
    // Detect format support
    await this.detectFormatSupport();

    // Add support class to document
    if (this.formats.avif) {
      document.documentElement.classList.add('supports-avif');
    }
    if (this.formats.webp) {
      document.documentElement.classList.add('supports-webp');
    }

    // Setup blur-up placeholders
    this.setupBlurUpLoading();

    // Setup responsive image sizing
    this.setupResponsiveSizing();

    // Monitor new images added dynamically
    this.observeNewImages();
  }

  /**
   * Detect browser support for modern image formats
   */
  async detectFormatSupport() {
    // Check AVIF support
    const avifTest = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A=';
    this.formats.avif = await this.canUseImageFormat(avifTest);

    // Check WebP support
    const webpTest = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=';
    this.formats.webp = await this.canUseImageFormat(webpTest);
  }

  /**
   * Test if browser can decode image format
   */
  canUseImageFormat(base64) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img.width === 1);
      img.onerror = () => resolve(false);
      img.src = base64;
    });
  }

  /**
   * Setup blur-up progressive loading for images
   */
  setupBlurUpLoading() {
    const images = document.querySelectorAll('img[data-blur-up]');

    images.forEach(img => {
      // Create low-quality placeholder
      const placeholder = img.getAttribute('data-blur-src');
      if (placeholder) {
        img.src = placeholder;
        img.classList.add('blur-up');
      }

      // Load full quality image
      const fullSrc = img.getAttribute('data-src');
      if (fullSrc) {
        const fullImg = new Image();
        fullImg.onload = () => {
          img.src = fullSrc;
          img.classList.remove('blur-up');
          img.classList.add('blur-up-loaded');
        };
        fullImg.src = fullSrc;
      }
    });
  }

  /**
   * Setup responsive image sizing based on container
   */
  setupResponsiveSizing() {
    if (!window.ResizeObserver) return;

    const images = document.querySelectorAll('img[data-responsive-size]');

    images.forEach(img => {
      const observer = new ResizeObserver(entries => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          this.updateImageSize(img, width);
        }
      });

      observer.observe(img.parentElement);
    });
  }

  /**
   * Update image URL to match container width
   */
  updateImageSize(img, containerWidth) {
    const baseUrl = img.getAttribute('data-base-url');
    if (!baseUrl) return;

    // Calculate optimal width (account for device pixel ratio)
    const dpr = window.devicePixelRatio || 1;
    const targetWidth = Math.ceil(containerWidth * dpr);

    // Round to standard Shopify image widths
    const standardWidths = [180, 360, 540, 720, 900, 1080, 1296, 1512, 1728, 1944, 2160, 2376, 2592, 2808, 3024];
    const optimalWidth = standardWidths.find(w => w >= targetWidth) || 3024;

    // Determine best format
    let format = 'jpg';
    if (this.formats.avif) {
      format = 'avif';
    } else if (this.formats.webp) {
      format = 'webp';
    }

    // Update image source
    const newUrl = baseUrl
      .replace('{width}', optimalWidth)
      .replace('{format}', format);

    if (img.src !== newUrl) {
      img.src = newUrl;
    }
  }

  /**
   * Observe DOM for new images and optimize them
   */
  observeNewImages() {
    if (!window.MutationObserver) return;

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) { // Element node
            const images = node.querySelectorAll ? node.querySelectorAll('img[data-blur-up], img[data-responsive-size]') : [];

            images.forEach(img => {
              if (img.hasAttribute('data-blur-up')) {
                this.setupBlurUpLoading();
              }
              if (img.hasAttribute('data-responsive-size')) {
                this.setupResponsiveSizing();
              }
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Preload critical images
   */
  preloadCriticalImages(imageUrls) {
    imageUrls.forEach(url => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      document.head.appendChild(link);
    });
  }

  /**
   * Get optimal image URL for current context
   */
  getOptimalImageUrl(baseUrl, width, height) {
    const dpr = window.devicePixelRatio || 1;
    const targetWidth = Math.ceil(width * dpr);
    const targetHeight = height ? Math.ceil(height * dpr) : null;

    let format = 'jpg';
    if (this.formats.avif) {
      format = 'avif';
    } else if (this.formats.webp) {
      format = 'webp';
    }

    let url = baseUrl + `?width=${targetWidth}`;
    if (targetHeight) {
      url += `&height=${targetHeight}`;
    }
    url += `&format=${format}`;

    return url;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.mdImageOptimizer = new ImageOptimizer();
  });
} else {
  window.mdImageOptimizer = new ImageOptimizer();
}

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ImageOptimizer;
}
